import { motion } from 'framer-motion'
import styled from 'styled-components'
import * as Accordion from '@radix-ui/react-accordion'
import { ArrowDownIcon } from '@velasca/icons/arrow-down'
import { CloseIcon } from '@velasca/icons/close'
import { FilterIcon as FilterIconBase } from '@velasca/icons/filter'
import { UnstyledList } from '@velasca/ui/unstyled-list'

export const Container = styled.div<{
  topDistance: number
  isSticky: boolean
}>`
  position: sticky;
  top: ${({ topDistance }) => `${topDistance}px`};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: ${({ theme }) => theme.fonts.brand};
  z-index: 10;
  padding: 2rem;
  background-color: ${({ theme, isSticky }) =>
    isSticky ? theme.colors.boxBackground : theme.colors.modalBackground};
  box-shadow: ${({ isSticky }) =>
    isSticky ? `0 2px 14px rgba(0, 0, 0, 0.3)` : 'none'};

  @media (min-width: 992px) {
    background-color: ${({ theme }) => theme.colors.background};
    position: relative;
    top: auto;
    gap: 0;
    margin: 0 auto;
    padding: 0;
    box-shadow: none;
  }
`

export const DesktopFilterButtonContainer = styled(motion.div)<{
  topDistance: number
}>`
  position: fixed;
  top: ${({ topDistance }) => `${topDistance}px`};
  left: 1.8rem;
  right: 0;
  font-family: ${({ theme }) => theme.fonts.brand};
  z-index: 10;
  max-width: 1200px;
  margin: 1.8rem auto;
  pointer-events: none;
`

export const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 0 3rem;
  overflow-x: auto;
  scrollbar-width: none;

  @media (min-width: 992px) {
    flex-wrap: wrap;
    gap: 1.5rem;
    max-width: 1200px;
    padding: 0;
    margin: 0 auto;
  }

  &::-webkit-scrollbar {
    width: 0 !important;
  }

  > * {
    flex: none;
  }
`

export const ColorBackground = styled.div<{
  isSticky: boolean
}>`
  position: relative;
  padding: 1rem 0;
  margin: 0 -2rem;

  @media (min-width: 992px) {
    background-color: ${({ theme }) => theme.colors.boxBackground};
    padding: 2rem 0 2rem 0;
    margin: 0;
  }

  @media (max-width: 991px) {
    padding: 1rem 0 0 0;
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 3rem;
      z-index: 10;
    }

    &::before {
      left: 0;
      background: linear-gradient(
        to right,
        ${({ theme, isSticky }) =>
            isSticky
              ? theme.colors.boxBackground
              : theme.colors.modalBackground}
          70%,
        transparent
      );
    }

    &::after {
      right: 0;
      background: linear-gradient(
        to left,
        ${({ theme, isSticky }) =>
            isSticky
              ? theme.colors.boxBackground
              : theme.colors.modalBackground}
          70%,
        transparent
      );
    }
  }
`

export const RemoveRefinementBadge = styled.button`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  border-radius: 0.5rem;
  text-transform: uppercase;
  line-height: 1.42857143;

  @media (min-width: 992px) {
    padding: 0.5rem 1.5rem;
  }
`

export const ActiveRefinementLabel = styled.div`
  display: flex;
  align-items: center;
  text-transform: uppercase;
  font-weight: 400;
  line-height: 1.5;
`

export const ClearRefinementsButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  color: ${({ theme }) => theme.colors.danger};
  background-color: transparent;
  border: none;
  text-transform: uppercase;
`

export const Close = styled.button`
  position: absolute;
  top: 2rem;
  right: 3rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  z-index: 1000;
`

export const FacetValuesBackground = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  background-color: ${({ theme }) => theme.colors.background};

  @media (min-width: 992px) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1;
    background-color: ${({ theme }) => theme.colors.boxBackground};
    box-shadow: 0px 20px 15px -15px rgba(0, 0, 0, 0.05);
  }
`

export const SkeletonContainer = styled.div`
  display: flex;
  height: 2.4rem;
  position: relative;
  align-items: center;
  gap: 1rem;
  margin: 0 0 2rem 0;

  @media (min-width: 992px) {
    margin: 0;
  }
`

export const StyledCheckbox = styled.div<{
  isChecked: boolean
  bulletColor?: string
}>`
  display: flex;
  height: 2.4rem;
  position: relative;
  align-items: center;
  &:not(:last-child) {
    margin: 0 0 1.7rem 0;
  }

  @media (min-width: 992px) {
    margin: 0 !important;
  }

  label,
  input {
    cursor: pointer;
  }

  input {
    width: 2.4rem;
    height: 2.4rem;
    padding: 0;
    opacity: 0;
    z-index: 20;
    margin-right: 1rem;
  }

  label {
    display: flex;
    align-items: center;
    margin: 0;
    font-size: 1.5rem;
    font-weight: ${({ isChecked }) => (isChecked ? 400 : 300)};
    color: ${({ isChecked, theme }) =>
      isChecked ? theme.colors.primary : theme.colors.text.primary};
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    display: block;
    border-radius: 10rem;
  }

  &::before {
    top: 0;
    left: 0;
    width: 2.4rem;
    height: 2.4rem;
    border-width: ${({ isChecked }) => (isChecked ? '0.25rem' : '0.125rem')};
    border-style: solid;
    border-color: ${({ isChecked, theme }) =>
      isChecked ? theme.colors.background : theme.colors.disabled};
    border-radius: 6rem;
    background-color: ${({ isChecked, bulletColor, theme }) =>
      isChecked && !bulletColor
        ? theme.colors.primary
        : (bulletColor ?? theme.colors.background)};
  }

  &::after {
    top: -0.2rem;
    left: -0.2rem;
    width: 2.8rem;
    height: 2.8rem;
    border-width: 0.2rem;
    border-style: solid;
    border-radius: 10rem;
    border-color: ${({ theme }) => theme.colors.border};
    opacity: ${({ isChecked }) => (isChecked ? 1 : 0)};
    transition: opacity 0.1618s;
  }
`

export const MainFilterContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
  font-family: ${({ theme }) => theme.fonts.brand};
  overflow-y: hidden;

  @media (min-width: 992px) {
    display: flex;
    justify-content: space-between;
    max-width: 1200px;
    padding: 0.8rem 1.5rem 0 1.5rem;
    margin: 0.1rem auto 0;
    overflow-y: auto;
  }
`

export const OpenFilterButton = styled.button<{ isCentered?: boolean }>`
  display: flex;
  position: relative;
  align-items: center;
  padding: 0.8rem 2rem;
  background: ${({ theme }) => theme.colors.boxBackground};
  border: none;
  font-size: 1.6rem;
  font-weight: 500;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  border: 1.5px solid ${({ theme }) => theme.colors.primary};
  border-radius: 3rem;
  line-height: 0;
  margin: ${({ isCentered }) => (isCentered ? '0 auto' : '0')};
  pointer-events: auto;

  &:active,
  &:hover {
    color: ${({ theme }) => theme.colors.background};
    background-color: ${({ theme }) => theme.colors.primary};
  }
`

export const FilterBy = styled.div`
  display: flex;
  align-items: center;
  padding: 2rem 0 2rem;
  background: transparent;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.5;
  text-transform: uppercase;
  padding-left: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (min-width: 992px) {
    padding: 0;
    margin-right: 1.5rem;
    border-bottom: none;
  }
`

export const PanelToggle = styled.button<{ isOpen: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  border: none;
  text-transform: uppercase;
  justify-content: center;
  padding: 1.5rem 2rem;
  background-color: ${({ isOpen, theme }) =>
    isOpen ? theme.colors.boxBackground : 'transparent'};
  border-radius: 0.5rem 0.5rem 0 0;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  transition: background-color 0.2s;
`

export const LeftSide = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 992px) {
    flex-direction: row;
    align-items: center;
  }
`

export const RightSide = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.8rem;
`

export const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

export const Chevron = styled(ArrowDownIcon)<{ $rotate?: boolean }>`
  color: currentColor;
  width: 1.2rem;
  margin-left: 1rem;
  transform-origin: 50% 50%;
  transform: ${({ $rotate }) => ($rotate ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
`

export const FacetToggle = styled(Accordion.Trigger)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
  text-transform: uppercase;

  &[data-state='open'] > ${Chevron} {
    transform: rotate(180deg);
  }
`

export const StyledClosedIcon = styled(CloseIcon)`
  margin-right: 0.3rem;
  display: flex;
`

export const ScrollableArea = styled(UnstyledList)`
  overflow-y: scroll;
  padding: 0 1.5rem 1rem 1.5rem;

  /* 64px is the height of the FilterBy component */
  height: calc(100dvh - 64px);
  @supports not (height: 100dvh) {
    height: calc(100vh - 64px);
  }
`

export const AccordionItem = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
  padding: 0.6rem 0;
`

export const FilterIcon = styled(FilterIconBase)`
  width: 1.6rem;
  margin-right: 1rem;
`

export const FacetsList = styled.div<{ isOpen: boolean }>`
  margin: 0.2rem;

  @media (min-width: 992px) {
    padding: 0.5rem 1.6rem;
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
  }
`

export const FacetsContainer = styled.div`
  display: flex;
  flex-flow: column wrap;
  gap: 2rem;
  padding: 2rem 0;

  @media (min-width: 992px) {
    gap: 1.5rem;
    padding: 1.75rem 0;
  }
`

export const FacetsTitle = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.4rem;
  font-weight: 500;
  text-transform: uppercase;
`

export const FacetsGroup = styled.div`
  @media (min-width: 992px) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 2rem;
  }

  // Questa regola fa in modo che il bordo appaia
  // solo se si trova a fianco dello stesso componente
  & + & {
    padding-top: 2rem;
    border-top: 0.1rem solid ${({ theme }) => theme.colors.border};

    @media (min-width: 992px) {
      padding-top: 0;
      border-top: none;
    }
  }
`

export const AccordionContent = styled(Accordion.Content)`
  overflow-y: hidden;

  &[data-state='open'] {
    animation: slideDown 0.8s ease-out;
  }
  &[data-state='closed'] {
    max-height: 0;
    animation: slideUp 0.2s ease-in;
  }

  @keyframes slideDown {
    from {
      max-height: 0;
    }
    to {
      /* 1000px is a arbitrary number, we just need to set a value that is bigger than the content. It's a trick to make the animation work. */
      max-height: 1500px;
    }
  }

  @keyframes slideUp {
    from {
      max-height: var(--radix-accordion-content-height);
    }
    to {
      max-height: 0;
    }
  }
`

export const RelativeContainer = styled.div`
  position: relative;
`

export const NoResults = styled.div`
  font-size: 1.6rem;
  text-align: center;
  padding: 8px;
`
